import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { assetsApi } from '../../services/assets';
import { employeesApi } from '../../services/employees';
import SearchableSelect from '../../components/SearchableSelect';

function AssetForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEdit = !!id;
  const preFilledEmployeeId = searchParams.get('employee_id');

  const OS_OPTIONS = [
    'Windows',
    'macOS',
    'Linux',
    'Ubuntu',
    'Fedora',
    'Chrome OS',
    'iOS',
    'Android',
    'Other'
  ];

  const [formData, setFormData] = useState({
    asset_type: 'laptop',
    make: '',
    model: '',
    serial_number: '',
    os: '',
    os_version: '',
    last_patch_date: '',
    antivirus_updated_date: '',
    status: !['hr_head', 'super_admin'].includes(user?.role) ? 'assigned' : 'available',
    assigned_to: preFilledEmployeeId || (!['hr_head', 'super_admin'].includes(user?.role) ? user?.id : ''),
    purchase_date: '',
    notes: '',
    location: ''
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchAsset();
    }
    // Only fetch employees list if user is an admin (not a regular employee)
    if (['hr_head', 'super_admin', 'admin_head', 'accounts_head'].includes(user?.role)) {
      fetchEmployees();
    }
  }, [id, user?.role]);

  const fetchAsset = async () => {
    try {
      const response = await assetsApi.getById(id);
      const data = response.data || response;
      console.log('Asset form data:', data);
      setFormData({
        ...data,
        last_patch_date: data.last_patch_date ? data.last_patch_date.split('T')[0] : '',
        purchase_date: data.purchase_date ? data.purchase_date.split('T')[0] : ''
      });
    } catch (err) {
      setError('Failed to load asset');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await employeesApi.getAll();
      const data = response.data || response;
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load employees');
      setEmployees([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate dates cannot be in the future (today's date is allowed)
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (formData.purchase_date) {
        const purchaseDate = new Date(formData.purchase_date);
        if (purchaseDate > today) {
          setError('Purchase date cannot be in the future');
          setLoading(false);
          return;
        }
      }

      if (formData.last_patch_date) {
        const patchDate = new Date(formData.last_patch_date);
        if (patchDate > today) {
          setError('Last patch date cannot be in the future');
          setLoading(false);
          return;
        }
      }

      if (formData.antivirus_updated_date) {
        const avDate = new Date(formData.antivirus_updated_date);
        if (avDate > today) {
          setError('Antivirus updated date cannot be in the future');
          setLoading(false);
          return;
        }
      }

      // Ensure non-admin users always send 'assigned' status and assign to themselves
      const submitData = {
        ...formData,
        status: !['hr_head', 'super_admin'].includes(user?.role) ? 'assigned' : formData.status,
        assigned_to: !['hr_head', 'super_admin'].includes(user?.role) ? user?.id : formData.assigned_to
      };

      if (isEdit) {
        await assetsApi.update(id, submitData);
      } else {
        await assetsApi.create(submitData);
      }
      navigate('/assets');
    } catch (err) {
      console.error('Error saving asset:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to save asset');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>{isEdit ? 'Edit Asset' : 'Add Asset'}</h2>
      </div>

      <div className="card">
        {error && (
          <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="asset_type">Asset Type *</label>
              <select
                id="asset_type"
                name="asset_type"
                className="form-control"
                value={formData.asset_type}
                onChange={handleChange}
                required
              >
                <option value="laptop">Laptop</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
                <option value="tablet">Tablet</option>
                <option value="other">Other</option>
              </select>
            </div>
            {!['employee'].includes(user?.role) && (
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  className="form-control"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="available">Available</option>
                  <option value="assigned">Assigned</option>
                  <option value="company">Company Asset</option>
                  <option value="repair">In Repair</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="make">Make *</label>
              <input
                type="text"
                id="make"
                name="make"
                className="form-control"
                value={formData.make}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="model">Model *</label>
              <input
                type="text"
                id="model"
                name="model"
                className="form-control"
                value={formData.model}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="serial_number">Serial Number *</label>
              <input
                type="text"
                id="serial_number"
                name="serial_number"
                className="form-control"
                value={formData.serial_number}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="os">Operating System</label>
              <select
                id="os"
                name="os"
                className="form-control"
                value={formData.os}
                onChange={handleChange}
              >
                <option value="">Select OS</option>
                {OS_OPTIONS.map(os => (
                  <option key={os} value={os}>{os}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="os_version">OS Version</label>
              <input
                type="text"
                id="os_version"
                name="os_version"
                className="form-control"
                value={formData.os_version}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="purchase_date">Purchase Date</label>
              <input
                type="date"
                id="purchase_date"
                name="purchase_date"
                className="form-control"
                value={formData.purchase_date}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="last_patch_date">Last Patch Date</label>
              <input
                type="date"
                id="last_patch_date"
                name="last_patch_date"
                className="form-control"
                value={formData.last_patch_date}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="antivirus_updated_date">Antivirus Updated Date</label>
              <input
                type="date"
                id="antivirus_updated_date"
                name="antivirus_updated_date"
                className="form-control"
                value={formData.antivirus_updated_date}
                onChange={handleChange}
              />
            </div>
          </div>

          {isEdit && (
            <div className="form-group" style={{ maxWidth: '50%' }}>
              <label htmlFor="assigned_to">Assign to Employee</label>
              <SearchableSelect
                options={employees}
                value={formData.assigned_to || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, assigned_to: value }))}
                placeholder="Search employees by name or email..."
                displayField="name"
                subDisplayField="email"
              />
            </div>
          )}

          {formData.status === 'company' && (
            <div className="form-group">
              <label htmlFor="location">Location *</label>
              <input
                type="text"
                id="location"
                name="location"
                className="form-control"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., Conference Room A, Reception, Server Room"
                required={formData.status === 'company'}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              className="form-control"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary btn-compact"
              onClick={() => navigate('/assets')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-compact"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AssetForm;
